<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { clearArray, isNaN, maxDotNumber } from "@zyzgroup/core-common";
import { useSafeArea$ } from "@zyzgroup/core-web";
import Pop from "../pop/Pop.vue";
import SVGClose from "../svg/SVGClose.vue";
import SVGCleanLeft from "../svg/SVGCleanLeft.vue";
import Box from "../base/Box.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: {}
});

const props = withDefaults(
  defineProps<{
    min?: number;
    max?: number;
    title?: string;
  }>(),
  {
    title: "自定义金额"
  }
);

const emits = defineEmits<{
  (event: "open"): void;
  (event: "close"): void;
  (event: "number", n: number): void;
  (event: "error", msg: string): void;
}>();

const safeAreaBottom = ref("0px");
let subcription;
onMounted(() => {
  subcription = useSafeArea$().subscribe((s) => {
    safeAreaBottom.value = s.bottom;
  });
});
onUnmounted(() => {
  subcription?.unsubscribe();
});

// 小数点前数字
const dotBefore: number[] = [];
// 小数点后数字
const dotAfter: number[] = [];
// 是否点击了小数点
let hasTapDot = false;
// 最终结果
const result = ref("");

const popRef = ref();

const open = () => {
  clearArray(dotBefore);
  clearArray(dotAfter);
  hasTapDot = false;
  // if (props.min) {
  //   _setResult(props.min);
  // }
  _computeResult();
  popRef.value?.open();
};

const close = () => {
  popRef.value?.close();
};

const confirm = () => {
  if (result.value == "") {
    emits("error", `请输入金额！`);
    return;
  }
  const n = parseFloat(result.value);
  if (isNaN(n)) {
    emits("error", `${n} is NaN!`);
    return;
  }
  // min 拦截
  if (typeof props.min == "number") {
    const min = maxDotNumber(props.min, 2);
    if (!isNaN(n) && n < min) {
      emits("error", `${props.title} 不能小于 ${min}`);
      // _setResult(min);
      // _computeResult();
      return;
    }
  }
  // max 拦截
  if (typeof props.max == "number") {
    const max = maxDotNumber(props.max, 2);
    if (!isNaN(n) && n > max) {
      emits("error", `${props.title} 不能大于 ${max}`);
      // _setResult(max);
      // _computeResult();
      return;
    }
  }
  emits("number", n);
  close();
};

function _setResult(n: number) {
  n = maxDotNumber(n, 2);
  hasTapDot = String(n).indexOf(".") > -1;
  clearArray(dotBefore);
  clearArray(dotAfter);
  let b = Math.floor(n);
  let t = b % 10;
  while (t || b) {
    dotBefore.unshift(t);
    b = Math.floor(b / 10);
    t = b % 10;
  }
  let a = maxDotNumber(n - Math.floor(n), 2);
  let i = 1;
  t = Math.floor(a * Math.pow(10, i));
  while (t || a) {
    dotAfter.push(t);
    a = maxDotNumber(a - t / Math.pow(10, i), 2);
    i++;
    t = Math.floor(a * Math.pow(10, i));
  }
}

function _computeResult() {
  result.value = "";
  dotBefore.forEach((d) => {
    result.value += "" + d;
  });
  if (hasTapDot) {
    result.value += ".";
  }
  // 只保留2位小数
  for (let i = 0, c = Math.min(dotAfter.length, 2); i < c; i++) {
    result.value += "" + dotAfter[i];
  }
}

const _forward = (v: number | string) => {
  if (typeof v == "number") {
    if (!hasTapDot) {
      dotBefore.push(v);
    } else {
      if (dotAfter.length < 2) {
        dotAfter.push(v);
      }
    }
  } else {
    hasTapDot = true;
  }
  _computeResult();
};

const _back = () => {
  if (dotAfter.length) {
    dotAfter.pop();
  } else if (hasTapDot) {
    hasTapDot = false;
  } else if (dotBefore.length) {
    dotBefore.pop();
  }
  _computeResult();
};

defineExpose({ open, close });
</script>

<template>
  <Pop
    ref="popRef"
    directionFrom="bottom"
    :global="true"
    :closeWhenClickOverlay="false"
    @open="() => emits('open')"
    @close="() => emits('close')"
  >
    <div class="number-keyboard">
      <!-- 头部 -->
      <div class="top">
        <div @click="close">
          <div style="width: 12px"><SVGClose></SVGClose></div>
        </div>
        <div>{{ title }}</div>
        <div @click="confirm">完成</div>
      </div>
      <!-- 结果显示 -->
      <div class="result">
        <!-- <div><input type="number" step="any" :value="result" disabled /></div> -->
        <div>
          <div class="input">
            <span>{{ result }}</span>
            <span class="cursor">
              <span class="line"></span>
            </span>
          </div>
        </div>
      </div>
      <!-- 选择项 -->
      <div
        class="numbers"
        :style="{
          paddingBottom: safeAreaBottom
        }"
      >
        <div>
          <div class="item">
            <Box
              :enableClickEffect="true"
              @throttleClick="() => _forward(1)"
            >
              <div class="item-inner">1</div>
            </Box>
          </div>
          <div class="item">
            <Box
              :enableClickEffect="true"
              @throttleClick="() => _forward(2)"
            >
              <div class="item-inner">2</div>
            </Box>
          </div>
          <div class="item">
            <Box
              :enableClickEffect="true"
              @throttleClick="() => _forward(3)"
            >
              <div class="item-inner">3</div>
            </Box>
          </div>
          <div class="item">
            <Box
              :enableClickEffect="true"
              @throttleClick="() => _forward(4)"
            >
              <div class="item-inner">4</div>
            </Box>
          </div>
          <div class="item">
            <Box
              :enableClickEffect="true"
              @throttleClick="() => _forward(5)"
            >
              <div class="item-inner">5</div>
            </Box>
          </div>
          <div class="item">
            <Box
              :enableClickEffect="true"
              @throttleClick="() => _forward(6)"
            >
              <div class="item-inner">6</div>
            </Box>
          </div>
          <div class="item">
            <Box
              :enableClickEffect="true"
              @throttleClick="() => _forward(7)"
            >
              <div class="item-inner">7</div>
            </Box>
          </div>
          <div class="item">
            <Box
              :enableClickEffect="true"
              @throttleClick="() => _forward(8)"
            >
              <div class="item-inner">8</div>
            </Box>
          </div>
          <div class="item">
            <Box
              :enableClickEffect="true"
              @throttleClick="() => _forward(9)"
            >
              <div class="item-inner">9</div>
            </Box>
          </div>
          <div class="item">
            <Box
              :enableClickEffect="true"
              @throttleClick="() => _forward('.')"
            >
              <div class="item-inner no-bg">.</div>
            </Box>
          </div>
          <div class="item">
            <Box
              :enableClickEffect="true"
              @throttleClick="() => _forward(0)"
            >
              <div class="item-inner">0</div>
            </Box>
          </div>
          <div class="item">
            <Box
              :enableClickEffect="true"
              @throttleClick="_back"
            >
              <div class="item-inner no-bg">
                <div style="width: 20px"><SVGCleanLeft></SVGCleanLeft></div>
              </div>
            </Box>
          </div>
        </div>
      </div>
    </div>
  </Pop>
</template>

<style scoped>
@keyframes cursor-blinks {
  0% {
    opacity: 1;
    display: block;
  }
  50% {
    opacity: 0;
    display: none;
  }
  100% {
    opacity: 1;
    display: block;
  }
}

.number-keyboard {
  box-sizing: border-box;
  width: 100%;
  background-color: #ffffff;
  color: #333333;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}
.top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 56px;
  line-height: 56px;
  padding: 0 15px;
  border-bottom: 1px solid #e6e6e6;
}
.top > div:nth-child(1) {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: 10px;
}
.top > div:nth-child(2) {
  flex: 1 1 auto;
  text-align: center;
  font-size: 20px;
  font-weight: bold;
  color: #333333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-wrap: normal;
  word-break: break-all;
}
.top > div:nth-child(3) {
  flex: 0 0 auto;
  font-size: 16px;
  font-weight: bold;
  color: #5a82f0;
  padding-left: 10px;
}
.result {
  padding: 15px;
}
.result > div {
  overflow: hidden;
  display: flex;
}
.result > div > .input {
  position: relative;
  flex: 1 1 auto;
  height: 44px;
  line-height: 44px;
  border: 1px solid #c8dcff;
  background: #e6f0ff;
  border-radius: 4px;
  font-size: 20px;
  font-weight: bold;
  color: #5a82f0;
  padding: 0 15px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
}
.cursor {
  width: 0;
  overflow: visible;
  position: relative;
}
.line {
  position: absolute;
  width: 1px;
  height: 24px;
  background: #5a82f0;
  left: 0;
  top: 50%;
  transform: translate(0, -50%);
  animation: cursor-blinks 1.5s infinite steps(1, start);
}
.numbers {
  background-color: #c6cad1;
}
.numbers > div {
  display: flex;
  flex-wrap: wrap;
  padding: 4px;
}
.item {
  flex: 0 0 33%;
  padding: 4px;
  box-sizing: border-box;
}
.item-inner {
  background: #ffffff;
  width: 100%;
  height: 44px;
  line-height: 44px;
  text-align: center;
  color: #333333;
  font-size: 20px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px #aaaaaa;
}
.item-inner.no-bg {
  background: rgba(255, 255, 255, 0);
  box-shadow: none;
}
</style>
